import { AlertTriangle, CheckCircle, Clock, Wrench } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { h2, h3, p, textStyle } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import Card from '../ui/card';
import './InvisibleCostTracker.css';
import { listUtilityAlerts, updateUtilityAlert } from './invisibleCostApi';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const UtilityAlertsPanel = () => {
  // Log screen view
  useScreenLogging('UtilityAlertsPanel', {
    component: 'UtilityAlertsPanel',
    screenType: 'utility-alerts',
  });

  const [alerts, setAlerts] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  // Fetch sites
  useEffect(() => {
    const loadSites = async () => {
      try {
        const sitesData = await getAllSites();
        setSites(sitesData || []);
      } catch (err) {
        console.error('Error loading sites:', err);
      }
    };
    loadSites();
  }, []);

  // Transform API alert data to component format
  const transformAlertData = useCallback((alert, sitesMap = {}) => {
    // Map site_id to site_name
    const siteName = alert.site_name || (alert.site_id && sitesMap[alert.site_id]?.site_name) || alert.site_id || 'Unknown Site';
    
    // Calculate spike percentage if threshold and current values are available
    let spikePercentage = null;
    if (alert.threshold_value && alert.current_value) {
      const threshold = alert.threshold_value;
      const current = alert.current_value;
      if (threshold > 0) {
        spikePercentage = ((current - threshold) / threshold) * 100;
      }
    }

    // Determine alert type based on available data
    const alertType = alert.alert_type || (spikePercentage !== null ? 'spike' : 'leakage');

    return {
      alert_id: alert.alert_id || alert.id || alert.alertId,
      alert_type: alertType,
      severity: alert.severity || 'medium',
      utility_type: alert.utility_type || 'Unknown',
      site_id: alert.site_id,
      site_name: siteName,
      appliance_id: alert.appliance_id,
      appliance_name: alert.appliance_name || '—',
      spike_percentage: spikePercentage !== null ? Math.round(spikePercentage) : alert.spike_percentage,
      threshold: alert.threshold_value || alert.threshold,
      current_usage: alert.current_value || alert.current_usage,
      previous_usage: alert.previous_usage,
      period: alert.period,
      status: alert.status || 'open',
      maintenance_ticket_id: alert.maintenance_ticket_id,
      notes: alert.notes,
      created_at: alert.created_at || alert.createdAt,
      timeline: alert.timeline || (alert.created_at ? [{
        status: 'detected',
        timestamp: alert.created_at,
        user: 'System'
      }] : []),
    };
  }, []);

  // Fetch alerts
  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 🔍 DEBUG: Log API call parameters
      const apiParams = {
        siteId: selectedSite || undefined,
        status: statusFilter || undefined,
      };
      console.log('🔍 [Utility Alerts API] Fetching alerts with params:', apiParams);
      console.log('🔍 [Utility Alerts API] Endpoint: GET /utilities/alerts');
      
      const result = await listUtilityAlerts(apiParams);

      // ✅ DEBUG: Log API response
      console.log('✅ [Utility Alerts API] Response received:', {
        success: result.success,
        alertsCount: result.alerts?.length || 0,
        hasError: !!result.error,
        error: result.error,
        statusCode: result.status,
      });
      
      // 🔍 DEBUG: Log raw API response data structure
      console.log('🔍 [Utility Alerts API] Raw response data:', {
        data: result.data,
        alerts: result.alerts,
        fullResult: result,
      });

      if (!result.success) {
        console.error('❌ [Utility Alerts API] API call failed:', result.error);
        throw new Error(result.error || 'Failed to fetch utility alerts');
      }

      // Create sites map for quick lookup
      const sitesMap = {};
      sites.forEach((site) => {
        if (site.site_id) {
          sitesMap[site.site_id] = site;
        }
      });

      // Transform alerts data
      const transformedAlerts = (result.alerts || []).map((alert) => transformAlertData(alert, sitesMap));
      
      // ✅ DEBUG: Log transformed data
      console.log('✅ [Utility Alerts API] Transformed alerts:', {
        count: transformedAlerts.length,
        alerts: transformedAlerts.map(a => ({
          id: a.alert_id,
          type: a.alert_type,
          severity: a.severity,
          status: a.status,
          utility_type: a.utility_type,
          site_name: a.site_name,
        })),
      });
      
      setAlerts(transformedAlerts);
    } catch (err) {
      console.error('❌ [Utility Alerts API] Error loading alerts:', err);
      setError(err.message || 'Failed to load alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSite, statusFilter, sites, transformAlertData]);

  useEffect(() => {
    console.log('🚀 [Utility Alerts API] Component mounted, loading alerts...');
    loadAlerts();
  }, [loadAlerts]);

  const handleAlertAction = async (alertId, action) => {
    setActionLoading((prev) => ({ ...prev, [alertId]: true }));
    
    try {
      let updates = {};
      
      if (action === 'acknowledge') {
        updates.status = 'acknowledged';
      } else if (action === 'resolve') {
        updates.status = 'resolved';
      } else if (action === 'open_ticket') {
        // In a real implementation, this would create a maintenance ticket first
        const ticketId = `ticket-${Date.now()}`;
        updates.status = 'acknowledged';
        updates.maintenance_ticket_id = ticketId;
      }

      // 🔍 DEBUG: Log update API call
      console.log('🔍 [Utility Alerts API] Updating alert:', {
        alertId,
        action,
        updates,
        endpoint: `PUT /utilities/alerts/${alertId}`,
      });

      const result = await updateUtilityAlert(alertId, updates);

      // ✅ DEBUG: Log update response
      console.log('✅ [Utility Alerts API] Update response:', {
        success: result.success,
        hasError: !!result.error,
        error: result.error,
        statusCode: result.status,
        updatedAlert: result.alert,
      });

      if (result.success) {
        console.log('✅ [Utility Alerts API] Alert updated successfully, reloading alerts...');
        // Reload alerts to get updated data
        await loadAlerts();
      } else {
        console.error('❌ [Utility Alerts API] Update failed:', result.error);
        setError(result.error || 'Failed to update alert status. Please try again.');
      }
    } catch (err) {
      console.error('❌ [Utility Alerts API] Error updating alert:', err);
      setError(err.message || 'Failed to update alert status');
    } finally {
      setActionLoading((prev) => ({ ...prev, [alertId]: false }));
    }
  };

  const getSeverityChipClass = (severity) => {
    switch (severity) {
      case 'high':
        return 'utility-alert-severity-high';
      case 'medium':
        return 'utility-alert-severity-medium';
      case 'low':
        return 'utility-alert-severity-low';
      default:
        return 'utility-alert-severity-medium';
    }
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'open':
        return 'utility-alert-status-open';
      case 'acknowledged':
        return 'utility-alert-status-acknowledged';
      case 'resolved':
        return 'utility-alert-status-resolved';
      default:
        return 'utility-alert-status-open';
    }
  };

  const getTimelineIcon = (status) => {
    switch (status) {
      case 'detected':
        return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      case 'acknowledged':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'maintenance_ticket_created':
        return <Wrench className="w-4 h-4 text-purple-600" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <div className="utility-alert-timeline-dot" />;
    }
  };

  if (loading && alerts.length === 0) {
    return (
      <div className="utility-alerts-panel">
        <div className="utility-matrix-loading">
          <div className="invisible-cost-spinner" />
          <p className={textStyle(p, 'text-gray-600 mt-4')}>Loading alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="utility-alerts-panel">
      <header className="utility-alerts-header">
        <h1 className={textStyle(h2, 'text-gray-900')}>Utility Alerts & Notifications</h1>
        <p className={textStyle(p, 'text-gray-600 mt-2')}>
          Monitor spikes, leakages, and consumption anomalies
        </p>
      </header>

      {/* Filters */}
      <div className="utility-alerts-filters">
        <select
          value={selectedSite}
          onChange={(e) => setSelectedSite(e.target.value)}
          className="utility-matrix-select"
        >
          <option value="">All Sites</option>
          {Array.isArray(sites) && [...sites]
                    .sort((a, b) => {
            const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
            const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((site) => (
                      <option key={site.site_id || site.id} value={site.site_id || site.id}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="utility-matrix-select"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {error && (
        <div className="utility-matrix-error mb-4">
          <p className={textStyle(p, 'text-red-600')}>{error}</p>
        </div>
      )}

      {/* Alerts List */}
      <div className="utility-alerts-grid">
        {alerts.length === 0 ? (
          <Card variant="light" className="p-8 text-center utility-alerts-empty">
            <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={textStyle(p, 'text-gray-600')}>No alerts found</p>
          </Card>
        ) : (
          alerts.map((alert) => (
            <Card key={alert.alert_id} variant="light" className="utility-alert-card">
              <div className="utility-alert-card-header">
                <div className="utility-alert-title">
                  <span className={`utility-alert-severity-chip ${getSeverityChipClass(alert.severity)}`}>
                    {alert.severity}
                  </span>
                  <h3 className={textStyle(h3, 'text-gray-900')}>
                    {alert.alert_type === 'spike' ? 'Spike Detected' : 'Leakage Alert'}
                  </h3>
                </div>
                <span className={`utility-alert-status ${getStatusChipClass(alert.status)}`}>
                  {alert.status}
                </span>
              </div>

              <div className="utility-alert-body">
                <div className="utility-alert-info-row">
                  <span className="utility-alert-info-label">Utility Type:</span>
                  <span>{alert.utility_type}</span>
                </div>
                <div className="utility-alert-info-row">
                  <span className="utility-alert-info-label">Site:</span>
                  <span>{alert.site_name}</span>
                </div>
                <div className="utility-alert-info-row">
                  <span className="utility-alert-info-label">Appliance:</span>
                  <span>{alert.appliance_name || '—'}</span>
                </div>
                {(alert.alert_type === 'spike' || alert.spike_percentage !== null) && (
                  <>
                    {alert.spike_percentage !== null && alert.spike_percentage !== undefined && (
                      <div className="utility-alert-info-row">
                        <span className="utility-alert-info-label">Spike Percentage:</span>
                        <span className="font-semibold text-red-600">
                          {alert.spike_percentage >= 0 ? '+' : ''}{alert.spike_percentage}%
                        </span>
                      </div>
                    )}
                    {alert.threshold !== null && alert.threshold !== undefined && (
                      <div className="utility-alert-info-row">
                        <span className="utility-alert-info-label">Threshold:</span>
                        <span>{alert.threshold}</span>
                      </div>
                    )}
                    {alert.current_usage !== null && alert.current_usage !== undefined && (
                      <div className="utility-alert-info-row">
                        <span className="utility-alert-info-label">Current Value:</span>
                        <span>{alert.current_usage} {alert.utility_type === 'electricity' ? 'kWh' : alert.utility_type === 'water' ? 'L' : 'units'}</span>
                      </div>
                    )}
                    {alert.previous_usage !== null && alert.previous_usage !== undefined && (
                      <div className="utility-alert-info-row">
                        <span className="utility-alert-info-label">Previous Value:</span>
                        <span>{alert.previous_usage} {alert.utility_type === 'electricity' ? 'kWh' : alert.utility_type === 'water' ? 'L' : 'units'}</span>
                      </div>
                    )}
                  </>
                )}
                {alert.period && (
                  <div className="utility-alert-info-row">
                    <span className="utility-alert-info-label">Period:</span>
                    <span>{alert.period}</span>
                  </div>
                )}
                {alert.notes && (
                  <div className="utility-alert-info-row">
                    <span className="utility-alert-info-label">Notes:</span>
                    <span className="text-gray-700">{alert.notes}</span>
                  </div>
                )}
                {alert.maintenance_ticket_id && (
                  <div className="utility-alert-info-row">
                    <span className="utility-alert-info-label">Maintenance Ticket:</span>
                    <span className="font-semibold text-blue-600">{alert.maintenance_ticket_id}</span>
                  </div>
                )}
              </div>

              {/* Timeline */}
              {alert.timeline && alert.timeline.length > 0 && (
                <div className="utility-alert-timeline">
                  <h4 className="utility-alert-timeline-title">Status Timeline</h4>
                  {alert.timeline.map((item, idx) => (
                    <div key={idx} className="utility-alert-timeline-item">
                      {getTimelineIcon(item.status)}
                      <div className="utility-alert-timeline-content">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold capitalize">{item.status.replace(/_/g, ' ')}</span>
                          <span className="text-gray-500 text-xs">{formatDate(item.timestamp)}</span>
                        </div>
                        {item.user && (
                          <p className="text-sm text-gray-600">by {item.user}</p>
                        )}
                        {item.ticket_id && (
                          <p className="text-sm text-blue-600">Ticket: {item.ticket_id}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="utility-alert-actions">
                {alert.status === 'open' && (
                  <>
                    <button
                      onClick={() => handleAlertAction(alert.alert_id, 'open_ticket')}
                      disabled={actionLoading[alert.alert_id]}
                      className="utility-alert-action-button utility-alert-action-button-primary"
                    >
                      <Wrench className="w-4 h-4 inline mr-2" />
                      {actionLoading[alert.alert_id] ? 'Processing...' : 'Open Maintenance Ticket'}
                    </button>
                    <button
                      onClick={() => handleAlertAction(alert.alert_id, 'acknowledge')}
                      disabled={actionLoading[alert.alert_id]}
                      className="utility-alert-action-button utility-alert-action-button-secondary"
                    >
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      Acknowledge
                    </button>
                  </>
                )}
                {alert.status === 'acknowledged' && (
                  <button
                    onClick={() => handleAlertAction(alert.alert_id, 'resolve')}
                    disabled={actionLoading[alert.alert_id]}
                    className="utility-alert-action-button utility-alert-action-button-primary"
                  >
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                    {actionLoading[alert.alert_id] ? 'Processing...' : 'Resolve'}
                  </button>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default UtilityAlertsPanel;
